<template>
  <div v-if="stockMatchCars.length > 0" class="stock-proposal-segment">
    <stock-match-card
      reference
      :title="i18n.t('/stockmatch/referencetitle')"
      :image-url="images.exterior || undefined"
      :display-name="displayName"
      :price="configuredPrice"
      :engine="configuredCarEngine"
      :rows="referenceRows"
    />
    <div class="stock-proposal-segment__matches">
      <button
        v-if="canScrollPrev"
        type="button"
        class="stock-proposal-segment__nav stock-proposal-segment__nav--prev"
        :aria-label="i18n.t('/stockmatch/previousmatches')"
        @click="scrollBy(-1)"
      >
        <icon icon="angle-left" :width="16" :height="16" />
      </button>
      <ul ref="scroller" class="stock-proposal-segment__scroller" @scroll="updateScrollState">
        <li v-for="(car, index) in stockMatchCars" :key="car.CommissionNumber">
          <stock-match-card
            :title="i18n.t('/stockmatch/matchtitle', { number: index + 1 })"
            :image-url="car.ImageExterior || undefined"
            :display-name="`${configuration.ModelName} ${car.Trimline}`"
            :price="parseFloat(car.ConsumerPrice)"
            :price-difference="parseFloat(car.ConsumerPrice) - configuredPrice"
            :engine="configuredCarEngine"
            :origin="stockOrigin(car)"
            :kom-number="car.CommissionNumber"
            :rows="stockMatchRows(car)"
            :extras="stockMatchExtras(car)"
            @stockcar-details="$emit('stockcar-details', car)"
            @reservation-status="$emit('reservation-status', car.CommissionNumber)"
            @select-car="$emit('select-car', car)"
          />
        </li>
      </ul>
      <button
        v-if="canScrollNext"
        type="button"
        class="stock-proposal-segment__nav stock-proposal-segment__nav--next"
        :aria-label="i18n.t('/stockmatch/nextmatches')"
        @click="scrollBy(1)"
      >
        <icon icon="angle-right" :width="16" :height="16" />
      </button>
    </div>
    <nav v-if="stockMatchCars.length > visibleCount" class="stock-proposal-segment__dots">
      <button
        v-for="(car, index) in stockMatchCars"
        :key="car.CommissionNumber"
        type="button"
        :aria-label="`${index + 1} / ${stockMatchCars.length}`"
        :aria-current="isVisible(index)"
        :class="{ 'stock-proposal-segment__dot--visible': isVisible(index) }"
        @click="scrollTo(index)"
      ></button>
    </nav>
  </div>
</template>

<script>
import StockMatchCard from '@/components/generic/presentational/molecules/stockMatchCard/StockMatchCard.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import { mapState } from 'vuex'
import StockMatchOptions from '@/mixins/stockMatchOptions'

export default {
  components: {
    StockMatchCard,
    Icon,
  },
  mixins: [StockMatchOptions],
  emits: ['open-modal', 'stockcar-details', 'reservation-status', 'select-car'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      firstVisible: 0,
      visibleCount: 2,
      canScrollNext: false,
    }
  },
  computed: {
    ...mapState('stockcars', ['matchingStockCars']),
    ...mapState('configuration', ['configuration', 'price', 'images']),
    displayName() {
      return `${this.configuration.ModelName || ''} ${this.configuration.TrimlineName || ''}`.trim()
    },
    configuredPrice() {
      return parseFloat(this.price.Incl)
    },
    canScrollPrev() {
      return this.firstVisible > 0
    },
  },
  watch: {
    stockMatchCars() {
      this.$nextTick(() => {
        this.$refs.scroller?.scrollTo({ left: 0 })
        this.updateScrollState()
      })
    },
  },
  mounted() {
    window.addEventListener('resize', this.updateScrollState)
    this.$nextTick(this.updateScrollState)
  },
  unmounted() {
    window.removeEventListener('resize', this.updateScrollState)
  },
  methods: {
    cardStep() {
      const scroller = this.$refs.scroller
      const card = scroller?.firstElementChild
      if (!card) return 1
      return card.offsetWidth + parseFloat(getComputedStyle(scroller).columnGap || 0)
    },
    updateScrollState() {
      const scroller = this.$refs.scroller
      if (!scroller) return
      this.firstVisible = Math.round(scroller.scrollLeft / this.cardStep())
      this.canScrollNext = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 4
    },
    scrollBy(cards) {
      this.$refs.scroller.scrollBy({ left: cards * this.cardStep(), behavior: 'smooth' })
    },
    scrollTo(index) {
      this.$refs.scroller.scrollTo({ left: index * this.cardStep(), behavior: 'smooth' })
    },
    isVisible(index) {
      return index >= this.firstVisible && index < this.firstVisible + this.visibleCount
    },
  },
}
</script>

<style lang="scss" scoped>
$gap: 1.25rem;

.stock-proposal-segment {
  display: grid;
  grid-template-columns: calc((100% - 2 * #{$gap}) / 3) 1fr;
  gap: $gap;
  margin: 1rem 0 2rem;
}

.stock-proposal-segment__matches {
  position: relative;
  min-width: 0;
}

.stock-proposal-segment__scroller {
  list-style: none;
  display: flex;
  column-gap: $gap;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > li {
    flex: 0 0 calc((100% - #{$gap}) / 2);
    min-width: 0;
    scroll-snap-align: start;
  }
}

.stock-proposal-segment__nav {
  position: absolute;
  top: 40%;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 1px solid var(--ds-color-border);
  border-radius: 50%;
  background: var(--ds-color-surface);
  color: var(--ds-color-text);
  box-shadow: var(--ds-shadow-sm);
  cursor: pointer;

  &--prev {
    left: -1.125rem;
  }

  &--next {
    right: -1.125rem;
  }
}

.stock-proposal-segment__dots {
  grid-column: 2;
  display: flex;
  justify-content: center;
  gap: var(--ds-space-2);

  button {
    width: 8px;
    height: 8px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--ds-color-border);
    cursor: pointer;
  }

  .stock-proposal-segment__dot--visible {
    background: var(--ds-color-brand);
  }
}
</style>
